import { useQuery } from '@tanstack/react-query'
import { getReportAccess } from '../services/reportAccessApi'
import { errorKind } from '../utils/format'
import { useLighthouseAdmin } from './useLighthouseAdmin'
import { adminPageMayFetch } from '../utils/adminGate'

/**
 * One report's access explanation, fetched only when a report is chosen and the
 * caller is not known to lack Lighthouse.Admin.
 *
 * `unknown` still fetches: the gate is unknown when the catalogue failed or
 * predates the permission, and a missing answer must never lock an administrator
 * out — the endpoint's own 403 is then rendered as restricted
 * (useLighthouseAdmin.ts). `pending` does not: the answer is still on its way.
 *
 * No poll. Grants change when a person changes them in Cartographer, and a
 * drawer somebody is reading must not re-sort under them; reopening refetches
 * once the default staleTime has passed.
 *
 * No retry on a 4xx: a 403, 401, 404 or 400 will not change on a second try, and
 * retrying one only delays the state the reader should see.
 */
export function useReportAccess(reportId: string | null) {
  const gate = useLighthouseAdmin()
  const enabled = Boolean(reportId) && adminPageMayFetch(gate)

  return useQuery({
    queryKey: ['observatory', 'reportAccess', reportId],
    queryFn: () => getReportAccess(reportId!),
    enabled,
    retry: (failureCount, error) => {
      const kind = errorKind(error)
      if (kind === 'forbidden' || kind === 'unauthorized' || kind === 'not-found' || kind === 'bad-request') {
        return false
      }
      return failureCount < 1
    },
  })
}
